import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Form, Select, Table, Spin, Typography, DatePicker } from 'antd'
import locale from 'antd/es/date-picker/locale/ru_RU'
import _ from 'lodash'
import ProjectSelector from '../../ProjectSelector'
import {
  APP_STATUS_AGREE,
  APP_STATUS_DELIVERY,
  APP_STATUS_DONE,
  APP_STATUS_PAY,
  PAYMENT_TYPE,
  statuses,
} from '../constants'
import { getReportGroupByProviders } from '../../../services/application'
import useTableFilter from '../../hooks/useTableFilter'
import { priceSumVat } from '../../../utils/calculation'
import HighlighterWrapper from '../../HighlighterWrapper'
import { formatDateTime, formatDate, dateRange } from '../../../utils/date-format'
import { userSelector } from '../../../store/session'
import { getUserProjectNameById } from '../report-utils'
import exportToXLS from './exportToXLS'
import { formatMoney } from '../../../utils/number-utils'

const { RangePicker } = DatePicker
const { Text } = Typography

const DEFAULT_HEIGHT = window.innerHeight || 600

const DATE_FORMAT = 'DD.MM.YYYY'

const defaultStatuses = [APP_STATUS_AGREE, APP_STATUS_PAY, APP_STATUS_DELIVERY, APP_STATUS_DONE]

const getCellStyling = (index, arr) => ({
  borderBottom: arr.length - 1 > index ? '1px solid #f0f0f0' : 0,
  padding: 8,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
})

export const getPaymentType = (type) => {
  const paymentType = PAYMENT_TYPE[type]
  return paymentType ? paymentType.caption : ''
}

const buildFilter = ({ project, statuses, appDates, payDates }) => ({
  project,
  statuses,
  createdAt: dateRange(appDates),
  pDate: dateRange(payDates),
})

const calcTransportationVatSum = (vatType, vatValue, price) => {
  if (vatType === 'up' && vatValue > 0 && price > 0) {
    const vat = (price / 100) * vatValue
    return price + vat
  } else {
    return price
  }
}

const transformAppResult = (results) => {
  let transformed = _.mapValues(_.groupBy(results, 'appId'), (items) => {
    let length = items.length
    let tVatSum = _.sumBy(items[0].t, ({ tVatType, tVatValue, tPrice }) =>
      calcTransportationVatSum(tVatType, tVatValue, tPrice)
    )
    let appSumVat = _.sumBy(items, 'sumVat') + tVatSum
    return items.map((item, idx) => ({
      appRowSpan: idx === 0 ? length : 0,
      appSumVat: idx === 0 ? appSumVat : 0,
      tVatSum: idx === 0 ? tVatSum : 0,
      ...item,
    }))
  })
  return _.flatten(Object.values(transformed))
}

const transformResult = (results) => {
  let i = 1
  let transformed = _.mapValues(
    _.groupBy(
      results.map((item) => ({
        idx: i++,
        sumVat: priceSumVat({
          amount: item.implAmount,
          price: item.implPrice,
          vatType: item.implVatType,
          vatValue: item.implVatValue,
        }),
        ...item,
      })),
      'provider'
    ),
    (items) => {
      let length = items.length
      let appTransformed = transformAppResult(items)
      let totalSumVat = _.sumBy(appTransformed, (item) => item.sumVat + item.tVatSum)
      return appTransformed.map((item, idx) => ({
        providerRowSpan: item.provider != null ? (idx === 0 ? length : 0) : 1,
        totalSumVat: idx === 0 ? totalSumVat : 0,
        ...item,
      }))
    }
  )
  return _.flatten(Object.values(transformed))
}

const FIELD_FILTERS = {
  'implementation-provider': (record, value) =>
    _.get(record, 'provider')?.toString().toLowerCase().includes(value.toLowerCase()),
  'impl-payment-type': (record, value) => _.get(record, 'implPaymentType') === value,
  'implementation-name': (record, value) =>
    _.get(record, 'implName')?.toString().toLowerCase().includes(value.toLowerCase()),
}

const filterData = (data, filters) => {
  const f = Object.entries(filters)
    .filter(([key, value]) => value)
    .map(([key, arr]) => {
      return (record) => arr.some((value) => FIELD_FILTERS[key](record, value))
    })

  return f.length > 0 ? data.filter((row) => f.every((filter) => filter(row))) : data
}

export const buildTransportsText = (t) =>
  _.join(_.uniq(t.map(({ tFrom, tTo }) => ` из ${_.trim(tFrom)} в ${_.trim(tTo)}`)))

const TableSummary = ({ data }) => {
  const totalTransportSum = _.sumBy(data, 'tVatSum')
  const totalSumVat = _.sumBy(data, 'sumVat')
  const totalSum = _.sumBy(data, 'totalSumVat')
  return (
    <Table.Summary>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={5}>
          <Text strong>Итого:</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right">
          <Text strong>{formatMoney(totalTransportSum)}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right" colSpan={2}>
          <Text strong>{formatMoney(totalSumVat)}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right" colSpan={5}>
          <Text strong>{formatMoney(totalSum)}</Text>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  )
}

const ProviderReport = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [xScrollEnabled, setXScrollEnabled] = useState(false)
  const [form] = Form.useForm()
  const [data, setData] = useState([])
  const [filters, setFilters] = useState({})
  const [filter, searchText, searchedColumn, getColumnSearchProps] = useTableFilter()
  const [project, setProject] = useState(null)

  const { user } = useSelector(userSelector)

  const columns = [
    {
      title: '№ п/п',
      key: 'idx',
      dataIndex: 'idx',
      align: 'right',
      width: 60,
    },
    {
      title: 'Поставщик',
      key: 'implementation-provider',
      dataIndex: 'provider',
      width: 400,
      ...getColumnSearchProps('provider'),
      onCell: ({ providerRowSpan }) => ({ rowSpan: providerRowSpan }),
      render: (value) =>
        searchedColumn === 'provider' ? (
          <HighlighterWrapper searchWords={[searchText]} textToHighlight={value ? value.toString() : ''} />
        ) : (
          value
        ),
    },
    {
      title: 'Заявка',
      key: 'application-id',
      dataIndex: 'appId',
      align: 'right',
      width: 100,
      render: (application_id) => (
        <a href={`/applications?state=all&id=${application_id}`} target="_blank" rel="noreferrer">
          {application_id}
        </a>
      ),
    },
    {
      title: 'Наименование',
      key: 'implementation-name',
      dataIndex: 'implName',
      align: 'left',
      width: 300,
      ...getColumnSearchProps('implName'),
      render: (value) =>
        searchedColumn === 'implName' ? (
          <HighlighterWrapper searchWords={[searchText]} textToHighlight={value ? value.toString() : ''} />
        ) : (
          value
        ),
    },
    {
      title: 'Доставка',
      key: 'transportation_cause',
      dataIndex: 't',
      align: 'left',
      width: 300,
      onCell: ({ appRowSpan }) => ({ rowSpan: appRowSpan }),
      render: (t) =>
        t.length > 0 && (
          <>
            <Text strong>Доставка:</Text>
            <Text>{buildTransportsText(t)}</Text>
          </>
        ),
    },
    {
      title: 'Доставка с НДС',
      key: 'transportation_vat',
      dataIndex: 'tVatSum',
      align: 'right',
      onCell: ({ appRowSpan }) => ({ rowSpan: appRowSpan }),
      render: (value) => formatMoney(value),
    },
    {
      title: 'Дата заявки',
      key: 'application-date',
      dataIndex: 'appDate',
      align: 'center',
      render: formatDateTime,
    },
    {
      title: 'Сумма с НДС',
      key: 'implementation_sum_vat',
      dataIndex: 'sumVat',
      align: 'right',
      render: (value) => formatMoney(value),
    },
    {
      title: 'Оплата',
      dataIndex: 'implPaymentType',
      key: 'impl-payment-type',
      filters: Object.entries(PAYMENT_TYPE).map(([key, value]) => ({ value: key, text: value.caption })),
      render: getPaymentType,
    },
    {
      title: 'Дата оплаты',
      dataIndex: 'payments',
      key: 'payment-date',
      align: 'center',
      className: 'no-padding-cell-report',
      onCell: ({ appRowSpan }) => ({ rowSpan: appRowSpan }),
      render: (payments) => (
        <div className="full-height-div">
          {payments?.map((item, index, arr) => (
            <div key={index} style={getCellStyling(index, arr)}>
              {formatDate(item?.pDate)}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: 'Сумма оплаты',
      dataIndex: 'payments',
      key: 'payment-amount',
      align: 'right',
      className: 'no-padding-cell-report',
      onCell: ({ appRowSpan }) => ({ rowSpan: appRowSpan }),
      render: (payments) => (
        <div className="full-height-div">
          {payments?.map((item, index, arr) => (
            <div key={index} style={getCellStyling(index, arr, 'right')}>
              {formatMoney(item?.pAmount)}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: 'Сумма заявки с НДС',
      key: 'app_sum_vat',
      dataIndex: 'appSumVat',
      align: 'right',
      render: (value) => formatMoney(value),
      onCell: ({ appRowSpan }) => ({ rowSpan: appRowSpan }),
    },
    {
      title: 'Итого с НДС',
      key: 'implementation_total_sum_vat',
      dataIndex: 'totalSumVat',
      align: 'right',
      render: (value) => formatMoney(value),
      onCell: ({ providerRowSpan }) => ({ rowSpan: providerRowSpan }),
    },
  ]

  const onFormSubmit = (values) => {
    setIsLoading(true)
    setProject(values.project)

    getReportGroupByProviders(buildFilter(values))
      .then((response) => {
        const results = response?.data
        if (results) {
          setData(results)
        }
      })
      .catch(() => {
        console.warn('Loading report result error')
      })
      .finally(() => setIsLoading(false))
  }

  let ds = useMemo(() => {
    return transformResult(filterData(data, filters))
  }, [data, filters])

  const onChange = (pagination, filters) => setFilters(filters)

  const exportData = useCallback(() => {
    let projectName = getUserProjectNameById(user, project)
    exportToXLS(ds, projectName, 'Отчет_по_поставщикам.xlsx')
  }, [ds, user, project])

  useEffect(() => {
    if (!isLoading && data && data.length > 0) {
      setXScrollEnabled(true)
    }
  }, [isLoading, data])

  return (
    <Spin spinning={isLoading}>
      <div style={{ paddingTop: 10 }}>
        <Form
          initialValues={{ statuses: defaultStatuses }}
          form={form}
          layout="vertical"
          style={{ display: 'flex' }}
          onFinish={onFormSubmit}
        >
          <Form.Item
            style={{ width: 300, maxWidth: 300 }}
            rules={[{ required: true, message: 'Выберите проект' }]}
            label="Проект"
            name="project"
          >
            <ProjectSelector />
          </Form.Item>
          <Form.Item style={{ marginLeft: 15 }} label="Статусы заявки" name="statuses">
            <Select mode="multiple" allowClear options={statuses} dropdownMatchSelectWidth={false} />
          </Form.Item>
          <Form.Item label="Даты заявки" style={{ marginLeft: 15, maxWidth: 240 }} name="appDates">
            <RangePicker locale={locale} format={DATE_FORMAT} />
          </Form.Item>
          <Form.Item label="Даты оплаты" style={{ marginLeft: 15, maxWidth: 240 }} name="payDates">
            <RangePicker locale={locale} format={DATE_FORMAT} />
          </Form.Item>
          <Form.Item label="" style={{ marginLeft: 15, marginTop: 30 }}>
            <Button onClick={() => form.submit()}>Сформировать отчет</Button>
          </Form.Item>
          <Button style={{ marginLeft: 15, marginTop: 30 }} disabled={data.length === 0} onClick={exportData}>
            Скачать
          </Button>
        </Form>
        <Table
          id="projectProviderTable"
          className="table-provider"
          columns={columns}
          dataSource={ds}
          bordered
          size="small"
          pagination={false}
          scroll={{ x: xScrollEnabled ? 3300 : undefined, y: DEFAULT_HEIGHT - 240 }}
          onChange={onChange}
          summary={(pageData) => <TableSummary data={pageData} />}
        />
      </div>
    </Spin>
  )
}

export default ProviderReport
